import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Save, BookOpen, Briefcase, Building2, Network } from "lucide-react";
import Clusters from "./Clusters";

const TABS = [
  { id: "books", label: "Books", icon: BookOpen },
  { id: "internal", label: "Internal Projects", icon: Briefcase },
  { id: "hubs", label: "Hubs", icon: Building2 },
  { id: "clusters", label: "Clusters", icon: Network },
];

export default function Options() {
  const [tab, setTab] = useState("books");
  return (
    <div className="max-w-[900px] mx-auto px-10 py-10 fade-in-up" data-testid="options-page">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Admin · Configuration
        </div>
        <h1 className="serif text-5xl tracking-tight leading-none">Options</h1>
        <p className="text-sm text-[color:var(--ink-2)] mt-3">
          Configure the values admins can pick from when tagging language engagements.
        </p>
      </div>

      <div className="flex border-b border-[color:var(--border)] mb-6" data-testid="options-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
              tab === t.id
                ? "border-[color:var(--ink)] text-[color:var(--ink)]"
                : "border-transparent text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon size={14} strokeWidth={1.5} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "books" && <BooksTab />}
      {tab === "internal" && <NamedListTab endpoint="/admin/internal-projects" label="Internal Project" testidPrefix="internal-project" />}
      {tab === "hubs" && <NamedListTab endpoint="/admin/hubs" label="Hub" testidPrefix="hub" />}
      {tab === "clusters" && (
        <div className="-mx-10" data-testid="clusters-tab">
          <Clusters />
        </div>
      )}
    </div>
  );
}


function BooksTab() {
  const [books, setBooks] = useState([]);
  const [newBook, setNewBook] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/books").then((r) => setBooks(r.data.books)).finally(() => setLoading(false));
  }, []);

  const add = () => {
    const v = newBook.trim();
    if (!v) return;
    if (books.includes(v)) { toast.error("Book already exists"); return; }
    setBooks([...books, v]);
    setNewBook("");
  };

  const remove = (b) => setBooks(books.filter((x) => x !== b));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/books", { books });
      toast.success("Book list saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="hairline bg-white p-6 mb-6">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-3">
          Active Books ({books.length})
        </div>
        {loading ? (
          <div className="mono text-xs">Loading…</div>
        ) : (
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {books.map((b) => (
              <li key={b} className="flex items-center justify-between border border-[color:var(--border)] px-3 py-2" data-testid={`book-${b}`}>
                <span className="text-sm">{b}</span>
                <button onClick={() => remove(b)} className="text-[color:var(--ink-3)] hover:text-[color:var(--behind)]" data-testid={`remove-${b}`}>
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex gap-3 mb-6">
        <input
          className="input-flat flex-1"
          placeholder="Add a book (e.g., Psalms)"
          value={newBook}
          onChange={(e) => setNewBook(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          data-testid="book-input"
        />
        <button className="btn-ghost flex items-center gap-1 text-sm" onClick={add} data-testid="add-book"><Plus size={14} /> Add</button>
      </div>

      <button className="btn-primary flex items-center gap-2 text-sm" onClick={save} disabled={saving} data-testid="save-books">
        <Save size={14} /> {saving ? "Saving…" : "Save Book List"}
      </button>
    </div>
  );
}


function NamedListTab({ endpoint, label, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint);
      setItems(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const add = async () => {
    const v = input.trim();
    if (!v) return;
    setBusy(true);
    try {
      await api.post(endpoint, { name: v });
      setInput("");
      toast.success(`${label} added`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Delete ${label} "${name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success(`${label} removed`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="hairline bg-white p-6 mb-6">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-3">
          Defined {label}s ({items.length})
        </div>
        {loading ? (
          <div className="mono text-xs">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[color:var(--ink-2)] italic">
            None defined yet. Add one below — it will appear as a dropdown option on the Language menu.
          </div>
        ) : (
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {items.map((it) => (
              <li key={it.id} className="flex items-center justify-between border border-[color:var(--border)] px-3 py-2" data-testid={`${testidPrefix}-item-${it.name}`}>
                <span className="text-sm">{it.name}</span>
                <button
                  onClick={() => remove(it.id, it.name)}
                  disabled={busy}
                  className="text-[color:var(--ink-3)] hover:text-[color:var(--behind)]"
                  data-testid={`${testidPrefix}-remove-${it.name}`}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex gap-3">
        <input
          className="input-flat flex-1"
          placeholder={`Add a ${label.toLowerCase()} (e.g., ${testidPrefix === 'hub' ? 'NILDAR' : 'Beyond2030'})`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !busy && add()}
          data-testid={`${testidPrefix}-input`}
        />
        <button className="btn-primary flex items-center gap-1 text-sm" onClick={add} disabled={busy} data-testid={`${testidPrefix}-add`}>
          <Plus size={14} /> Add
        </button>
      </div>
    </div>
  );
}
